import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Grid,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PrimaryButton, SecondaryButton } from '../../components/atoms/Button';
import axios from 'axios';
import useSnackbar from '../../hooks/useSnackbar';

interface AddRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface ToolRequest {
  tool_id: string;
  identifier: string;
  name: string;
  type: string;
  quantity: number;
  comment: string;
  reason: 'BROKEN' | 'LOST' | 'OTHER' | 'NEW_ASSIGNMENT';
}

interface Tool {
  _id: string;
  name: string;
  description?: string;
  image?: string;
  code?: string;
  active: boolean;
}

interface Technician {
  _id: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  position?: string;
  status?: string;
}

const AddRequestModal: React.FC<AddRequestModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [showSecondRequest, setShowSecondRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // Technician states
  const [technicianId, setTechnicianId] = useState('');
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [technicianPage, setTechnicianPage] = useState(1);
  const [technicianTotalPages, setTechnicianTotalPages] = useState(1);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [technicianSearch, setTechnicianSearch] = useState('');

  // First Request
  const [toolId1, setToolId1] = useState('');
  const [toolName1, setToolName1] = useState('');
  const [toolType1, setToolType1] = useState('');
  const [identifier1, setIdentifier1] = useState('');
  const [quantity1, setQuantity1] = useState(1);
  const [reason1, setReason1] = useState('');
  const [comment1, setComment1] = useState('');

  // Second Request
  const [toolId2, setToolId2] = useState('');
  const [toolName2, setToolName2] = useState('');
  const [toolType2, setToolType2] = useState('');
  const [identifier2, setIdentifier2] = useState('');
  const [quantity2, setQuantity2] = useState(1);
  const [reason2, setReason2] = useState('');
  const [comment2, setComment2] = useState('');

  // Tool list states
  const [tools1, setTools1] = useState<Tool[]>([]);
  const [tools2, setTools2] = useState<Tool[]>([]);
  const [toolPage1, setToolPage1] = useState(1);
  const [toolPage2, setToolPage2] = useState(1);
  const [toolTotalPages1, setToolTotalPages1] = useState(1);
  const [toolTotalPages2, setToolTotalPages2] = useState(1);
  const [loadingTools1, setLoadingTools1] = useState(false);
  const [loadingTools2, setLoadingTools2] = useState(false);
  const [toolSearch1, setToolSearch1] = useState('');
  const [toolSearch2, setToolSearch2] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Refs for scroll containers
  const technicianDropdownRef = useRef<HTMLDivElement>(null);
  const toolDropdown1Ref = useRef<HTMLDivElement>(null);
  const toolDropdown2Ref = useRef<HTMLDivElement>(null);

  const reasons = ['BROKEN', 'LOST', 'OTHER', 'NEW_ASSIGNMENT'];

  const getToken = () => {
    const storedData = localStorage.getItem('authData');
    if (storedData) {
      const parsed = JSON.parse(storedData);
      return parsed.token;
    }
    return null;
  };

  // API Calls
  const fetchToolsFromAPI = async (
    pageNum: number,
    search = '',
    limit = 10,
  ) => {
    try {
      const token = getToken();
      let url = `${process.env.REACT_APP_SERVER_API_BASE_URL}/api/v1/admin/tools/list?limit=${limit}&page=${pageNum}`;

      if (search) {
        url += `&search=${search}`;
      }
      // url += `&sortField=name&sortOrder=ASC`;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data.success) {
        return response.data;
      }
      throw new Error('Failed to fetch tools');
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  const fetchTechniciansFromAPI = async (
    pageNum: number,
    search = '',
    limit = 100,
  ) => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_SERVER_API_BASE_URL}/api/v1/admin/technician/list?limit=${limit}&page=${pageNum}`;

      const response = await axios.post(
        url,
        { search },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200 && response.data.status) {
        return response.data;
      }
      throw new Error('Failed to fetch technicians');
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  // Fetch technicians
  const fetchTechnicians = useCallback(async (pageNum: number, search = '') => {
    setLoadingTechnicians(true);
    try {
      const response = await fetchTechniciansFromAPI(pageNum, search);
      const newTechnicians = response?.data || [];
      const totalPages = response?.pagination?.totalPages || 1;

      if (pageNum === 1) {
        setTechnicians(newTechnicians);
      } else {
        setTechnicians((prev) => [...prev, ...newTechnicians]);
      }

      setTechnicianTotalPages(totalPages);
      setTechnicianPage(pageNum);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    } finally {
      setLoadingTechnicians(false);
    }
  }, []);

  // Fetch tools for first dropdown
  const fetchTools1 = useCallback(async (pageNum: number, search = '') => {
    setLoadingTools1(true);
    try {
      const response = await fetchToolsFromAPI(pageNum, search);
      const newTools = response?.data || [];
      const totalPages = response?.pagination?.totalPages || 1;

      if (pageNum === 1) {
        setTools1(newTools);
      } else {
        setTools1((prev) => [...prev, ...newTools]);
      }

      setToolTotalPages1(totalPages);
      setToolPage1(pageNum);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoadingTools1(false);
    }
  }, []);

  // Fetch tools for second dropdown
  const fetchTools2 = useCallback(async (pageNum: number, search = '') => {
    setLoadingTools2(true);
    try {
      const response = await fetchToolsFromAPI(pageNum, search);
      const newTools = response?.data || [];
      const totalPages = response?.pagination?.totalPages || 1;

      if (pageNum === 1) {
        setTools2(newTools);
      } else {
        setTools2((prev) => [...prev, ...newTools]);
      }

      setToolTotalPages2(totalPages);
      setToolPage2(pageNum);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoadingTools2(false);
    }
  }, []);

  // Initial load when modal opens
  useEffect(() => {
    if (open) {
      setTechnicians([]);
      setTools1([]);
      setTools2([]);
      setTechnicianPage(1);
      setToolPage1(1);
      setToolPage2(1);
      setTechnicianSearch('');
      setToolSearch1('');
      setToolSearch2('');
      setTechnicianId('');
      setToolId1('');
      setToolId2('');
      fetchTechnicians(1, '');
      fetchTools1(1, '');
    }
  }, [open]);

  // Handle scroll for technician dropdown
  const handleTechnicianScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      !loadingTechnicians &&
      technicianPage < technicianTotalPages
    ) {
      fetchTechnicians(technicianPage + 1, technicianSearch);
    }
  };

  // Handle scroll for tool dropdown 1
  const handleToolScroll1 = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      !loadingTools1 &&
      toolPage1 < toolTotalPages1
    ) {
      fetchTools1(toolPage1 + 1, toolSearch1);
    }
  };

  // Handle scroll for tool dropdown 2
  const handleToolScroll2 = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      !loadingTools2 &&
      toolPage2 < toolTotalPages2
    ) {
      fetchTools2(toolPage2 + 1, toolSearch2);
    }
  };

  // Handle search for technician
  const handleTechnicianSearch = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const query = event.target.value;
    setTechnicianSearch(query);
    setTechnicians([]);
    setTechnicianPage(1);
    setTechnicianTotalPages(1);
    fetchTechnicians(1, query);
  };

  // Handle search for tool dropdown 1
  const handleToolSearch1 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setToolSearch1(query);
    setTools1([]);
    setToolPage1(1);
    setToolTotalPages1(1);
    fetchTools1(1, query);
  };

  // Handle search for tool dropdown 2
  const handleToolSearch2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setToolSearch2(query);
    setTools2([]);
    setToolPage2(1);
    setToolTotalPages2(1);
    fetchTools2(1, query);
  };

  const handleTechnicianChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    console.log('Setting technician ID:', value);
    setTechnicianId(value);
  };

  const handleToolChange1 = (event: SelectChangeEvent) => {
    const value = event.target.value;
    console.log('Setting tool ID 1:', value);
    setToolId1(value);
    const selectedTool = tools1.find((tool) => tool._id === value);
    if (selectedTool) {
      setToolName1(selectedTool.name);
      setToolType1('TOOL');
      setIdentifier1(selectedTool.code || '');
    }
  };

  const handleToolChange2 = (event: SelectChangeEvent) => {
    const value = event.target.value;
    console.log('Setting tool ID 2:', value);
    setToolId2(value);
    const selectedTool = tools2.find((tool) => tool._id === value);
    if (selectedTool) {
      setToolName2(selectedTool.name);
      setToolType2('TOOL');
      setIdentifier2(selectedTool.code || '');
    }
  };

  const handleAddMore = (): void => {
    setShowSecondRequest(true);
    if (tools2.length === 0) {
      fetchTools2(1, '');
    }
  };

  const validateForm = (): boolean => {
    if (!technicianId) {
      showSnackbar('Please select technician', 'error');
      return false;
    }
    if (!toolId1 || !toolType1 || !identifier1 || !quantity1 || !reason1) {
      showSnackbar(
        'Please fill all required fields for first tool request',
        'error',
      );
      return false;
    }
    if (
      showSecondRequest &&
      (!toolId2 || !toolType2 || !identifier2 || !quantity2 || !reason2)
    ) {
      showSnackbar(
        'Please fill all required fields for second tool request',
        'error',
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const tools: ToolRequest[] = [
        {
          tool_id: toolId1,
          identifier: identifier1,
          name: toolName1,
          type: toolType1,
          quantity: quantity1,
          comment: comment1 || '',
          reason: reason1 as 'BROKEN' | 'LOST' | 'OTHER' | 'NEW_ASSIGNMENT',
        },
      ];

      if (showSecondRequest) {
        tools.push({
          tool_id: toolId2,
          identifier: identifier2,
          name: toolName2,
          type: toolType2,
          quantity: quantity2,
          comment: comment2 || '',
          reason: reason2 as 'BROKEN' | 'LOST' | 'OTHER' | 'NEW_ASSIGNMENT',
        });
      }

      const payload = { technicianId, tools };
      const token = getToken();

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_API_BASE_URL}/api/v1/admin/tool/request`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log('Response:', response.data);

      showSnackbar('Tool request submitted successfully!');

      // Reset form
      setTechnicianId('');
      setToolId1('');
      setToolName1('');
      setToolType1('');
      setIdentifier1('');
      setQuantity1(1);
      setReason1('');
      setComment1('');
      setToolId2('');
      setToolName2('');
      setToolType2('');
      setIdentifier2('');
      setQuantity2(1);
      setReason2('');
      setComment2('');
      setShowSecondRequest(false);

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error submitting request:', error);
      showSnackbar('Failed to submit request. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Simple Technician Dropdown - Fixed
  const renderTechnicianDropdown = () => {
    return (
      <FormControl fullWidth>
        <Select
          value={technicianId}
          onChange={handleTechnicianChange}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: '#666' }}>Select Technician</span>;
            }
            const selectedTech = technicians.find((t) => t._id === selected);
            return selectedTech?.name || '';
          }}
          sx={{
            height: '55px',
            backgroundColor: '#181818',
            color: '#fff',
            border: '1px solid #D9D9D9',
            borderRadius: '9px',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: '#fff' },
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Technician</em>
          </MenuItem>
          {technicians.map((tech) => (
            <MenuItem key={tech._id} value={tech._id}>
              {tech.name} {tech.phoneNumber ? `(${tech.phoneNumber})` : ''}
            </MenuItem>
          ))}
          {loadingTechnicians && (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ color: '#E31E25', mr: 1 }} />
              Loading...
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  };

  // Simple Tool Dropdown 1 - Fixed
  const renderToolDropdown1 = () => {
    return (
      <FormControl fullWidth>
        <Select
          value={toolId1}
          onChange={handleToolChange1}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: '#666' }}>Select Tool</span>;
            }
            const selectedTool = tools1.find((t) => t._id === selected);
            return selectedTool?.name || '';
          }}
          sx={{
            height: '55px',
            backgroundColor: '#181818',
            color: '#fff',
            border: '1px solid #D9D9D9',
            borderRadius: '9px',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: '#fff' },
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Tool</em>
          </MenuItem>
          {tools1.map((tool) => (
            <MenuItem key={tool._id} value={tool._id}>
              {tool.name} {tool.code ? `(${tool.code})` : ''}
            </MenuItem>
          ))}
          {loadingTools1 && (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ color: '#E31E25', mr: 1 }} />
              Loading...
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  };

  // Simple Tool Dropdown 2 - Fixed
  const renderToolDropdown2 = () => {
    return (
      <FormControl fullWidth>
        <Select
          value={toolId2}
          onChange={handleToolChange2}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: '#666' }}>Select Tool</span>;
            }
            const selectedTool = tools2.find((t) => t._id === selected);
            return selectedTool?.name || '';
          }}
          sx={{
            height: '55px',
            backgroundColor: '#181818',
            color: '#fff',
            border: '1px solid #D9D9D9',
            borderRadius: '9px',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: '#fff' },
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Tool</em>
          </MenuItem>
          {tools2.map((tool) => (
            <MenuItem key={tool._id} value={tool._id}>
              {tool.name} {tool.code ? `(${tool.code})` : ''}
            </MenuItem>
          ))}
          {loadingTools2 && (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ color: '#E31E25', mr: 1 }} />
              Loading...
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '30px',
          backgroundColor: '#181818',
          border: '1px solid #E31E25',
        },
      }}
    >
      {SnackbarComponent}
      <DialogTitle
        sx={{
          fontSize: '22px',
          fontWeight: 'bold',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#181818',
          padding: '16px',
          borderBottom: '1px solid #E31E25',
        }}
      >
        Add Request
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
        <Grid container spacing={2} className="pt-5">
          {/* Technician Name Field */}
          <Grid item xs={12}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Technician Name
            </Typography>
            {/* {renderTechnicianDropdown()} */}
            <Autocomplete
              options={Array.isArray(technicians) ? technicians : []}
              getOptionLabel={(option: any) =>
                `${option.name || 'N/A'} (${option.phoneNumber.slice(0, 10)})`
              } // Display name and phone number
              value={
                technicianId
                  ? technicians.find((t) => t._id === technicianId) || null
                  : null
              }
              onChange={(event, newValue) => {
                setTechnicianId(newValue?._id || '');
              }}
              onInputChange={(event, newInputValue) => {
                setTechnicianSearch(newInputValue);
                fetchTechnicians(1, newInputValue);
              }}
              sx={{
                // height: '55px',
                backgroundColor: '#181818',
                // color: '#fff',
                border: '1px solid #D9D9D9',
                borderRadius: '12px',
                // '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                // '& .MuiSelect-icon': { color: '#fff' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Technician"
                  sx={{
                    borderWidth: '1px',
                    borderColor: '#D9D9D9',
                    borderRadius: '12px',
                    backgroundColor: '#181818',
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiInputBase-input': { color: '#fff' },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </Grid>

          {/* First Request */}
          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Tool Name
            </Typography>
            {/* {renderToolDropdown1()} */}
            <Autocomplete
              options={Array.isArray(tools1) ? tools1 : []}
              getOptionLabel={(option: any) =>
                `${option.name} ${option.code ? `(${option.code})` : ''}`
              } // Display name and phone number
              value={
                toolId1 ? tools1.find((t) => t._id === toolId1) || null : null
              }
              onChange={(event, newValue) => {
                setToolId1(newValue?._id || '');
                const selectedTool = tools1.find(
                  (tool) => tool._id === newValue?._id,
                );
                if (selectedTool) {
                  setToolName1(selectedTool.name);
                  setToolType1('TOOL');
                  setIdentifier1(selectedTool.code || '');
                }
              }}
              onInputChange={(event, newInputValue) => {
                // setToolSearch(newInputValue);
                fetchTools1(1, newInputValue);
              }}
              sx={{
                // height: '55px',
                backgroundColor: '#181818',
                // color: '#fff',
                border: '1px solid #D9D9D9',
                borderRadius: '12px',
                // '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                // '& .MuiSelect-icon': { color: '#fff' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Tool"
                  sx={{
                    borderWidth: '1px',
                    borderColor: '#D9D9D9',
                    borderRadius: '12px',
                    backgroundColor: '#181818',
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiInputBase-input': { color: '#fff' },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Identifier
            </Typography>
            <input
              className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
              placeholder="Enter Identifier"
              value={identifier1}
              onChange={(e) => setIdentifier1(e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Tool Type
            </Typography>
            <input
              className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
              placeholder="Tool Type"
              value={toolType1}
              readOnly
            />
          </Grid>
          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Quantity
            </Typography>
            <input
              className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
              placeholder="Enter Quantity"
              type="number"
              min="1"
              value={quantity1}
              onChange={(e) => setQuantity1(parseInt(e.target.value) || 1)}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Reason
            </Typography>
            <FormControl fullWidth>
              <Select
                value={reason1}
                onChange={(e) => setReason1(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected)
                    return <span style={{ color: '#666' }}>Select Reason</span>;
                  return selected;
                }}
                sx={{
                  height: '55px',
                  backgroundColor: '#181818',
                  color: '#fff',
                  border: '1px solid #D9D9D9',
                  borderRadius: '9px',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-icon': { color: '#fff' },
                }}
              >
                <MenuItem value="" disabled>
                  Select Reason
                </MenuItem>
                {reasons.map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Comment
            </Typography>
            <input
              className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
              placeholder="Enter Comment"
              value={comment1}
              onChange={(e) => setComment1(e.target.value)}
            />
          </Grid>

          {/* Second Request */}
          {showSecondRequest && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ backgroundColor: '#E31E25', my: 2 }} />
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    mb: 2,
                  }}
                >
                  Tool Request 02
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography
                  gutterBottom
                  sx={{ color: '#fff', fontSize: '14px' }}
                >
                  Tool Name
                </Typography>
                {renderToolDropdown2()}
              </Grid>
              <Grid item xs={6}>
                <Typography
                  gutterBottom
                  sx={{ color: '#fff', fontSize: '14px' }}
                >
                  Identifier
                </Typography>
                <input
                  className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
                  placeholder="Enter Identifier"
                  value={identifier2}
                  onChange={(e) => setIdentifier2(e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography
                  gutterBottom
                  sx={{ color: '#fff', fontSize: '14px' }}
                >
                  Tool Type
                </Typography>
                <input
                  className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
                  placeholder="Tool Type"
                  value={toolType2}
                  readOnly
                />
              </Grid>
              <Grid item xs={6}>
                <Typography
                  gutterBottom
                  sx={{ color: '#fff', fontSize: '14px' }}
                >
                  Quantity
                </Typography>
                <input
                  className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
                  placeholder="Enter Quantity"
                  type="number"
                  min="1"
                  value={quantity2}
                  onChange={(e) => setQuantity2(parseInt(e.target.value) || 1)}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography
                  gutterBottom
                  sx={{ color: '#fff', fontSize: '14px' }}
                >
                  Reason
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={reason2}
                    onChange={(e) => setReason2(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected)
                        return (
                          <span style={{ color: '#666' }}>Select Reason</span>
                        );
                      return selected;
                    }}
                    sx={{
                      height: '55px',
                      backgroundColor: '#181818',
                      color: '#fff',
                      border: '1px solid #D9D9D9',
                      borderRadius: '9px',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '& .MuiSelect-icon': { color: '#fff' },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Reason
                    </MenuItem>
                    {reasons.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  gutterBottom
                  sx={{ color: '#fff', fontSize: '14px' }}
                >
                  Comment
                </Typography>
                <input
                  className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
                  placeholder="Enter Comment"
                  value={comment2}
                  onChange={(e) => setComment2(e.target.value)}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#181818',
          padding: '16px',
          display: 'flex',
          justifyContent: 'end',
          borderTop: '1px solid #E31E25',
        }}
      >
        <SecondaryButton
          title={'Add More'}
          onClick={handleAddMore}
          disabled={loading}
        />
        <PrimaryButton
          title={loading ? 'Saving...' : 'Save'}
          onClick={handleSubmit}
          disabled={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AddRequestModal;
